import { NodePositionChange } from '@xyflow/react';
import { useCallback, useRef } from 'react';
import { CanvasElementType, CanvasEvent, CanvasEventType } from '../../canvas-types';
import {
  RfDropZoneNode,
  RfNodeRegularNode,
  SweepCanvasRfNode,
} from '../../canvas-types/nodeTypesData';
import { snapPositionToGrid } from '../../helpers/gridPositioningUtils';
import { ObjectOperations } from '../objectOperations';
import { DropNodeStateEnum, useCalculateDragAndDrop } from '../../useCalculateDragAndDrop';
import { MovingNodesOriginalPositionRef, NodeTransformations } from '../useOnNodeChange';

import { updateGroupBoundingBox } from '../updateGroupBoundingBox';
import { getNormalizedGroupPositions } from '../../normalizeGroupPositions';
import { RFEdgeData, RFEdgeFloatingEdge } from '../../edges';
import { useSectionableNodeMoveEvent } from './useSectionableNodeMoveEvent';
import { useGetIntersectionSection } from '../../sections/useGetIntersectionSection';
import { useSweepCanvasState } from '../../internal-context/CanvasStateContext';

const DROPPING_ZONE_NODE_ID = 'dropping-zone-node';

/**
 * Hook to handle position changes for regular nodes (non-group nodes)
 * Handles both dragging and dropping of nodes within groups
 */
export const useOnRegularNodePositionChange = (
  movingNodes: React.MutableRefObject<MovingNodesOriginalPositionRef>,
) => {
  const { getDropNodeState } = useCalculateDragAndDrop();
  const { createSectionableNodeMoveEvent } = useSectionableNodeMoveEvent();
  const { getIntersectionSection } = useGetIntersectionSection();
  const { setCanvasEdges } = useSweepCanvasState();

  const temporaryEdges = useRef<RFEdgeFloatingEdge[] | null>(null);

  // after moving out of a swap position, restore the edges to their original state
  const restoreSwappedEdges = useCallback(
    (edgeOperations: ObjectOperations<RFEdgeFloatingEdge>) => {
      if (temporaryEdges.current) {
        temporaryEdges.current.forEach((edge) => {
          edgeOperations.setObject<RFEdgeFloatingEdge>(edge.id, {
            ...edge,
            data: { ...(edge.data as RFEdgeData), highlightType: null },
          });
        });
        temporaryEdges.current = null;
      }
    },
    [],
  );

  // while dragging, update the edges to point to the drop zone node
  const swapEdgesWithDropZoneNode = useCallback(
    (nodeChangeId: string, edgeOperations: ObjectOperations<RFEdgeFloatingEdge>) => {
      const { setObject: setEdge, getObjects: getEdges } = edgeOperations;
      if (!temporaryEdges.current) {
        const _swapEdges = getEdges().filter(
          (edge) => edge.source === nodeChangeId || edge.target === nodeChangeId,
        );
        if (_swapEdges.length) {
          temporaryEdges.current = _swapEdges;
          _swapEdges.forEach((edge) => {
            setEdge(edge.id, (edge) => {
              const _newEdge = {
                ...edge,
              };
              if (edge.source === nodeChangeId) {
                _newEdge.source = DROPPING_ZONE_NODE_ID;
              }
              if (edge.target === nodeChangeId) {
                _newEdge.target = DROPPING_ZONE_NODE_ID;
              }
              if (_newEdge.data) {
                _newEdge.data.highlightType = null;
              }
              return _newEdge;
            });
          });
        }
      }
    },
    [],
  );

  const onRegularNodePositionChange = useCallback(
    ({
      nodeChange,
      node,
      nodeOperations,
      nodeTransformations,
    }: {
      nodeChange: NodePositionChange;
      node: RfNodeRegularNode;
      nodeTransformations: NodeTransformations; // passed by reference for performance
      nodeOperations: ObjectOperations<SweepCanvasRfNode>; // passed by reference for performance
    }) => {
      const nodeChangeEvents: CanvasEvent[] = [];
      // Early return if no position change or node has no parent
      if (!nodeChange.position || !node.parentId) return;

      const {
        setObject: setNode,
        removeObject: removeNode,
        hasObject: hasNode,
        addObject: addNode,
        affectAllObjectsWithPartial,
      } = nodeOperations;

      // Check if drop position is valid
      const { dropState, nodeAtPosition } = getDropNodeState({
        newPotentialNodPos: nodeChange.position,
        id: nodeChange.id,
        allowSwap: true,
      });

      affectAllObjectsWithPartial((node) => {
        if (node.type === CanvasElementType.REGULAR && node.data.swapState) {
          return {
            ...node,
            data: { ...node.data, swapState: null },
          };
        }
        return node;
      });

      if (nodeChange.dragging) {
        setNode<RfNodeRegularNode>(node.id, (node) => ({
          ...node,
          zIndex: 1000,
          data: {
            ...node.data,
            dropState,
          },
        }));

        const updatedGroupDimensions = updateGroupBoundingBox({
          nodeOperations,
          groupNodeId: node.parentId,
        });
        if (!updatedGroupDimensions) return;

        // Add the drop zone node to the node transformations if it doesn't exist
        const dropZoneNodeXYPosition =
          dropState === DropNodeStateEnum.VALID
            ? snapPositionToGrid(nodeChange.position, {
                withPaddingByNodeType: CanvasElementType.REGULAR,
              })
            : movingNodes.current[node.id].originalPosition;

        if (nodeAtPosition && dropState === DropNodeStateEnum.VALID_SWITCH) {
          if (!hasNode(DROPPING_ZONE_NODE_ID)) {
            addNode({
              id: DROPPING_ZONE_NODE_ID,
              position: dropZoneNodeXYPosition,
              parentId: node.parentId,
              zIndex: 1,
              data: {
                type: 'swap',
                objectType: nodeAtPosition.data.objectType,
                name: nodeAtPosition.data.name,
              },
              type: CanvasElementType.DROP_ZONE_NODE,
            });
          } else {
            // Update the drop zone node position in nodeOverrides

            setNode<RfDropZoneNode>(DROPPING_ZONE_NODE_ID, (node) => ({
              ...node,
              position: dropZoneNodeXYPosition,
              zIndex: 1,
              data: {
                type: 'swap',
                objectType: nodeAtPosition.data.objectType,
                name: nodeAtPosition.data.name,
              },
            }));
          }
          setNode<RfNodeRegularNode>(nodeAtPosition.id, (node) => ({
            ...node,
            data: { ...node.data, swapState: 'target' },
          }));
          setCanvasEdges((edges, edgeOperations) => {
            swapEdgesWithDropZoneNode(node.id, edgeOperations);
            return edges;
          });
        } else {
          if (!hasNode(DROPPING_ZONE_NODE_ID)) {
            addNode({
              id: DROPPING_ZONE_NODE_ID,
              position: dropZoneNodeXYPosition,
              parentId: node.parentId,
              zIndex: 1,
              data: {
                type: 'regular',
              },
              type: CanvasElementType.DROP_ZONE_NODE,
            });
          } else {
            // Update the drop zone node position in nodeOverrides

            setNode<RfDropZoneNode>(DROPPING_ZONE_NODE_ID, (node) => ({
              ...node,
              position: dropZoneNodeXYPosition,
              zIndex: 1,
              data: {
                type: 'regular',
              },
            }));
          }
          setCanvasEdges((edges, edgeOperations) => {
            restoreSwappedEdges(edgeOperations);
            return edges;
          });
        }
      } else {
        // On drop
        if (
          dropState === DropNodeStateEnum.INVALID ||
          dropState === DropNodeStateEnum.VALID_SWITCH
        ) {
          // If invalid drop location, reset to original position
          nodeChange.position = movingNodes.current[node.id].originalPosition;
          setNode<RfNodeRegularNode>(node.id, (node) => {
            return {
              ...node,
              position: nodeChange.position!,
              data: {
                ...node.data,
                dropState: null,
              },
            };
          });
          removeNode(DROPPING_ZONE_NODE_ID);

          if (dropState === DropNodeStateEnum.VALID_SWITCH && nodeAtPosition) {
            nodeChangeEvents.push({
              type: CanvasEventType.SwapNodes,
              sourceNode: { id: node.id, parentId: node.parentId },
              targetNode: { id: nodeAtPosition.id, parentId: nodeAtPosition.parentId },
            });
          }
          movingNodes.current = {};
        } else {
          if (!nodeChange.position) return;

          nodeOperations.setObject<RfNodeRegularNode>(node.id, (node) => ({
            ...node,
            position: nodeChange.position ?? node.position,
          }));

          const { normalizedGroupPosition, normalizedStepPositions, groupNode } =
            getNormalizedGroupPositions({
              groupId: node.parentId,
              nodeOperations,
            });

          normalizedStepPositions.forEach(({ id, position }) => {
            nodeChangeEvents.push({
              type: CanvasEventType.MoveNode,
              parentId: node.parentId as string,
              nodeId: id,
              newPosition: position,
            });
          });

          if (groupNode.parentId) {
            const groupNodeChange: NodePositionChange = {
              id: groupNode.id,
              position: normalizedGroupPosition.xyPosition,
              type: 'position',
              dragging: false,
            };
            const intersectionSectionNode = getIntersectionSection(groupNode);
            const groupMoveEvent = createSectionableNodeMoveEvent({
              nodeChange: groupNodeChange,
              node: groupNode,
              nodeOperations,
              intersectionSectionNode,
            });

            if (groupMoveEvent) {
              nodeTransformations.extraNodePositionChanges.push(groupNodeChange);
              nodeChangeEvents.push({
                type: CanvasEventType.MoveGroup,
                nodeId: groupMoveEvent.nodeId,
                newPosition: groupMoveEvent.canvasGridPosition,
                parentId: groupMoveEvent.parentId,
              });
            }
          } else {
            nodeChangeEvents.push({
              type: CanvasEventType.MoveGroup,
              nodeId: normalizedGroupPosition.id,
              newPosition: normalizedGroupPosition.position,
              parentId: groupNode.parentId,
            });
          }
          // Reset moving nodes tracking
          movingNodes.current = {};
        }
        setCanvasEdges((edges, edgeOperations) => {
          restoreSwappedEdges(edgeOperations);
          return edges;
        });
      }
      return nodeChangeEvents;
    },
    [
      getDropNodeState,
      movingNodes,
      swapEdgesWithDropZoneNode,
      setCanvasEdges,
      restoreSwappedEdges,
      getIntersectionSection,
      createSectionableNodeMoveEvent,
    ],
  );

  return {
    onRegularNodePositionChange,
  };
};
